import React, { useState, useMemo, useCallback, useEffect } from 'react';
import AdminLayout from '../../components/AdminLayout';
import Loader from '../../components/Loader';
import AdminListViewControls from '../../components/AdminListViewControls';
import AdminSelectableList from '../../components/AdminSelectableList';
import { getFeedbacks } from '../../services/adminApi';
import { useAdminInfiniteList, useNearBottomScroll } from '../../hooks/useAdminInfiniteList';
import { useAdminListView } from '../../hooks/useAdminListView';
import { normalizeAdminListResponse } from '../../utils/adminListResponse';
import usePortalScope from '../../hooks/usePortalScope';
import './AdminPages.css';

const FeedbackList = () => {
  const portal = usePortalScope();
  const brands = portal.brands;
  const [exporting, setExporting] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [brandFilter, setBrandFilter] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  useEffect(() => {
    const handle = setTimeout(() => {
      setSearchTerm((prev) => (prev === searchInput ? prev : searchInput));
    }, 300);
    return () => clearTimeout(handle);
  }, [searchInput]);

  const filtersKey = useMemo(
    () => JSON.stringify({ statusFilter, brandFilter, searchTerm, dateFrom, dateTo }),
    [statusFilter, brandFilter, searchTerm, dateFrom, dateTo]
  );

  const getFeedbackParams = useCallback(() => {
    const params = {};
    if (statusFilter) params.status = statusFilter;
    if (portal.isSuperAdmin && brandFilter) params.brandId = brandFilter;
    if (searchTerm) params.search = searchTerm;
    if (dateFrom) params.dateFrom = dateFrom;
    if (dateTo) params.dateTo = dateTo;
    if (!statusFilter || statusFilter === 'all') {
      params.includeNotSubmitted = '1';
    }
    return params;
  }, [statusFilter, brandFilter, searchTerm, dateFrom, dateTo, portal]);

  const { items: feedbacks, loading, loadingMore, pagination, loadMore } = useAdminInfiniteList({
    load: getFeedbacks,
    resetKey: filtersKey,
    getParams: getFeedbackParams,
    pageSize: 30
  });

  const handleClearFilters = useCallback(() => {
    setStatusFilter('');
    setBrandFilter('');
    setSearchInput('');
    setSearchTerm('');
    setDateFrom('');
    setDateTo('');
  }, []);

  const scrollRef = useNearBottomScroll(loadMore, { enabled: !loading && pagination.hasMore });

  const listView = useAdminListView(feedbacks, (fb) => fb._id, {
    defaultView: 'table',
    resetKey: filtersKey
  });

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const escapeCsv = (v) => {
    const s = String(v == null ? '' : v);
    if (s.includes(',') || s.includes('"') || s.includes('\n')) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };

  const getCustomerName = (fb) =>
    fb.user?.profile?.name ||
    fb.user?.mobile ||
    fb.booking?.customer?.profile?.name ||
    fb.booking?.customer?.mobile ||
    'Customer';

  const feedbackColumns = useMemo(
    () => [
      { key: 'date', label: 'Date', render: (fb) => formatDate(fb.createdAt) },
      { key: 'customer', label: 'Customer', render: (fb) => getCustomerName(fb) },
      {
        key: 'mobile',
        label: 'Mobile',
        render: (fb) => fb.user?.mobile || fb.booking?.customer?.mobile || '-'
      },
      { key: 'service', label: 'Service', render: (fb) => fb.service?.name || '-' },
      {
        key: 'brand',
        label: 'Brand',
        render: (fb) => fb.booking?.salon?.brand?.name || '-'
      },
      {
        key: 'branch',
        label: 'Branch',
        render: (fb) => {
          const salon = fb.booking?.salon;
          if (!salon) return '-';
          return `${salon.name || '-'}${salon.branch ? ` (${salon.branch})` : ''}`;
        }
      },
      {
        key: 'stylistRating',
        label: 'Stylist',
        render: (fb) =>
          fb.stylistRating != null
            ? `${fb.stylistRating}/5 · ${fb.stylistName || fb.booking?.stylist?.id?.name || fb.booking?.stylist?.name || '-'}`
            : '-'
      },
      {
        key: 'serviceRating',
        label: 'Service Rating',
        render: (fb) => (fb.serviceRating != null ? `${fb.serviceRating}/5` : '-')
      },
      { key: 'status', label: 'Status', render: (fb) => fb.status || '-' }
    ],
    []
  );

  const feedbackListFooter = (
    <>
      {loadingMore && <p className="admin-infinite-loading">Loading more…</p>}
      {!pagination.hasMore && feedbacks.length > 0 && (
        <p className="admin-infinite-end">All items loaded ({pagination.total} total).</p>
      )}
    </>
  );

  const feedbackEmptyState = (
    <div className="admin-card empty-state">
      <p>No feedback found</p>
    </div>
  );

  const renderFeedbackCard = (fb) => (
    <div className="admin-card transaction-card">
      <div className="transaction-header">
        <div>
          <h3>{getCustomerName(fb)}</h3>
          <p className="transaction-date">{formatDate(fb.createdAt)}</p>
        </div>
      </div>
      <div className="transaction-details">
        <p>
          <strong>Service:</strong> {fb.service?.name || '-'}
        </p>
        {fb.service?.category?.name && (
          <p>
            <strong>Category:</strong> {fb.service.category.name}
          </p>
        )}
        {fb.booking?.salon && (
          <>
            <p>
              <strong>Brand:</strong> {fb.booking.salon?.brand?.name || '-'}
            </p>
            <p>
              <strong>Branch:</strong> {fb.booking.salon?.name || '-'}
              {fb.booking.salon?.branch && ` (${fb.booking.salon.branch})`}
            </p>
          </>
        )}
        <p>
          <strong>Stylist rating:</strong>{' '}
          {fb.stylistRating != null ? `${fb.stylistRating}/5` : '-'}
        </p>
        <p>
          <strong>Stylist:</strong>{' '}
          {fb.stylistName || fb.booking?.stylist?.id?.name || fb.booking?.stylist?.name || '-'}
        </p>
        <p>
          <strong>Service rating:</strong>{' '}
          {fb.serviceRating != null ? `${fb.serviceRating}/5` : '-'}
        </p>
        {fb.description && (
          <p>
            <strong>Comment:</strong> {fb.description}
          </p>
        )}
        {(fb.user?.mobile || fb.booking?.customer?.mobile) && (
          <p>
            <strong>Mobile:</strong> {fb.user?.mobile || fb.booking?.customer?.mobile}
          </p>
        )}
      </div>
    </div>
  );

  const exportFeedbackExcel = async () => {
    setExporting(true);
    try {
      const params = { ...getFeedbackParams(), all: true };
      const response = await getFeedbacks(params);
      const { data: list } = normalizeAdminListResponse(response);
      const headers = ['Date', 'Customer', 'Mobile', 'Transaction ID', 'Service', 'Category', 'Brand', 'Branch', 'Stylist', 'Stylist Rating', 'Service Rating', 'Comment', 'Status'];
      const rows = (list || []).map((fb) => [
      formatDate(fb.createdAt),
      fb.user?.profile?.name || '',
      fb.user?.mobile || '',
      fb.booking?.transaction?.transactionId || fb.booking?.payment?.transactionId || (fb.booking?._id ? `BK-${String(fb.booking._id).slice(-8).toUpperCase()}` : ''),
      fb.service?.name || '',
      fb.service?.category?.name || '',
      fb.booking?.salon?.brand?.name || '',
      [fb.booking?.salon?.name, fb.booking?.salon?.branch].filter(Boolean).join(' ') || '',
      fb.stylistName || fb.booking?.stylist?.id?.name || fb.booking?.stylist?.name || '',
      fb.stylistRating != null ? fb.stylistRating : '',
      fb.serviceRating != null ? fb.serviceRating : '',
      fb.description || '',
      fb.status || ''
    ]);
    const csv = [headers.map(escapeCsv).join(','), ...rows.map((r) => r.map(escapeCsv).join(','))].join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `feedback-export-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) {
      console.error('Export failed:', e);
    } finally {
      setExporting(false);
    }
  };

  return (
    <AdminLayout
      pageTitle="Feedback"
      subtitle={
        pagination.total
          ? `${feedbacks.length} of ${pagination.total} items · scroll for more`
          : 'View customer feedback and ratings'
      }
    >
      <div className="admin-page-container">
        <div className="admin-page-header">
          <h1 style={{ margin: 0 }}>Feedback List</h1>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={exportFeedbackExcel}
            disabled={exporting || loading}
          >
            {exporting ? 'Exporting…' : 'Download Excel (CSV)'}
          </button>
        </div>

        <div className="admin-card filter-card filter-card--feedback">
          <h3>Filters</h3>
          <div className="filter-grid filter-grid--feedback">
            <div className="form-group filter-grid--feedback-search">
              <label>Search</label>
              <input
                type="text"
                placeholder="Customer, service, comment…"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
            </div>
            <div className="form-group">
              <label>Date From</label>
              <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
            </div>
            <div className="form-group">
              <label>Date To</label>
              <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
            </div>
            {portal.showBrandPicker && (
            <div className="form-group">
              <label>Brand</label>
              <select
                value={brandFilter}
                onChange={(e) => setBrandFilter(e.target.value)}
              >
                <option value="">All Brands</option>
                {brands.map((b) => (
                  <option key={b._id} value={b._id}>
                    {b.name}
                  </option>
                ))}
              </select>
            </div>
            )}
            <div className="form-group">
              <label>
                Status
                <span title="Not Submitted: no feedback captured. Pending: submitted but not yet reviewed." style={{ marginLeft: '6px', cursor: 'help', opacity: 0.8 }}>ⓘ</span>
              </label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="">All</option>
                <option value="not_submitted">Not Submitted</option>
                <option value="pending">Pending</option>
              </select>
            </div>
            <div className="form-group filter-actions">
              <label htmlFor="feedback-clear-filters">&nbsp;</label>
              <button
                id="feedback-clear-filters"
                type="button"
                className="btn btn-secondary"
                onClick={handleClearFilters}
              >
                Clear
              </button>
            </div>
          </div>
         
        </div>

        {loading ? (
          <Loader variant="dark" fullScreen={false} />
        ) : (
          <>
            <AdminListViewControls
              viewMode={listView.viewMode}
              onViewModeChange={listView.setViewMode}
              selectedCount={listView.selectedCount}
              onSelectAll={listView.selectAll}
              onClearSelection={listView.clearSelection}
              allSelected={listView.allSelected}
              someSelected={listView.someSelected}
              totalCount={feedbacks.length}
            showSelection={false}
            />
            <AdminSelectableList
              items={feedbacks}
              getItemId={(fb) => fb._id}
              viewMode={listView.viewMode}
            selectable={false}
              isSelected={listView.isSelected}
              onToggleItem={listView.toggleItem}
              allSelected={listView.allSelected}
              someSelected={listView.someSelected}
              onSelectAll={listView.selectAll}
              onClearSelection={listView.clearSelection}
              columns={feedbackColumns}
              listClassName="transaction-list"
              renderGridCard={renderFeedbackCard}
              emptyState={feedbackEmptyState}
              scrollRef={scrollRef}
              footer={feedbackListFooter}
            />
          </>
        )}
      </div>
    </AdminLayout>
  );
};

export default FeedbackList;
